import type { FunctionComponent } from 'react';
import { Suspense, useCallback, useEffect } from 'react';

import { ChunkLoadErrorBoundary } from '@trello/error-boundaries';
import { useBoardId } from '@trello/id-context';
import { Dialog, useDialog } from '@trello/nachos/dialog';
import {
  getLocation,
  removeSearchParamsFromLocation,
  useSearchParams,
} from '@trello/router';
import { navigate } from '@trello/router/navigate';
import { useLazyComponent } from '@trello/use-lazy-component';

import { LazyBoardInviteModal } from 'app/src/components/BoardInviteModal';
import { LazyJoinBoardModal } from 'app/src/components/JoinBoardModal';
import { LazyRequestAccessConfirmationModal } from 'app/src/components/RequestAccessConfirmationModal';

export const ConditionalBoardOverlay: FunctionComponent = () => {
  const boardId = useBoardId();
  const { dialogProps, show } = useDialog();
  const searchParams = useSearchParams();

  const LegacyMemberBoardProfileView = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "legacy-board-member-profile-view" */ './LegacyMemberBoardProfileView'
      ),
    { preload: false, namedImport: 'LegacyMemberBoardProfileView' },
  );

  const closeOverlay = useCallback(
    (location: ReturnType<typeof getLocation> = getLocation()) => {
      location = removeSearchParamsFromLocation(location, ['overlay']);
      navigate(`${location.pathname}${location.search}`, { trigger: false });
    },
    [],
  );

  const closeRequestAccessOrBoardInviteOverlay = useCallback(() => {
    const location = removeSearchParamsFromLocation(getLocation(), [
      'inviteMemberId',
      'signature',
    ]);
    closeOverlay(location);
  }, [closeOverlay]);

  useEffect(() => {
    if (searchParams.get('overlay')) {
      show();
    }
  }, [searchParams, show]);

  useEffect(() => {
    if (searchParams.get('overlay') === 'request-access') {
      if (
        !searchParams.get('inviteMemberId') ||
        !searchParams.get('signature')
      ) {
        closeRequestAccessOrBoardInviteOverlay();
      }
    }
  }, [closeRequestAccessOrBoardInviteOverlay, searchParams]);

  switch (searchParams.get('overlay')) {
    case 'member-activity':
      return (
        <Suspense fallback={null}>
          <ChunkLoadErrorBoundary fallback={null}>
            <LegacyMemberBoardProfileView boardId={boardId} />
          </ChunkLoadErrorBoundary>
        </Suspense>
      );
    case 'share':
      return (
        <Dialog {...dialogProps} hide={closeRequestAccessOrBoardInviteOverlay}>
          <LazyBoardInviteModal
            idBoard={boardId}
            onClose={closeRequestAccessOrBoardInviteOverlay}
          />
        </Dialog>
      );
    case 'join':
      return (
        <Dialog
          {...dialogProps}
          closeOnOutsideClick={false}
          closeOnEscape={false}
          alignment="center"
        >
          <LazyJoinBoardModal idBoard={boardId} />
        </Dialog>
      );
    case 'request-access':
      return (
        <Dialog {...dialogProps} hide={closeRequestAccessOrBoardInviteOverlay}>
          <LazyRequestAccessConfirmationModal
            boardId={boardId}
            idMember={searchParams.get('inviteMemberId') as string}
            signature={searchParams.get('signature') as string}
            onClose={closeRequestAccessOrBoardInviteOverlay}
          />
        </Dialog>
      );
    default:
      return null;
  }
};
